import { useMemo, useState } from "react";
import { sectionRegistry } from "@a2z/storefront-schemas";
import type { SectionInstance } from "@a2z/storefront-schemas";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { ThemeDraftApi } from "../state/useThemeDraft";

/**
 * Ordered section list for the active template — the studio's left pane.
 *
 * Every capability question (can this be deleted? how many are allowed? what is
 * it called?) is answered by `sectionRegistry` from `@a2z/storefront-schemas`,
 * never by a local table. That is what keeps this pane from drifting away from
 * the seller editor and the storefront renderer.
 */

function sectionName(type: string, lang: "ar" | "en"): string {
  const schema = sectionRegistry.sections[type];
  return schema?.name?.[lang] ?? schema?.name?.en ?? type;
}

export function SectionList({
  draft,
  lang,
}: {
  draft: ThemeDraftApi;
  lang: "ar" | "en";
}) {
  const t = useTranslations();
  const [isAdding, setIsAdding] = useState(false);

  /** Section types that can still be added to this template: known to the
   *  registry, not locked, and not already at `maxPerTemplate`. */
  const addable = useMemo(() => {
    const counts = new Map<string, number>();
    for (const s of draft.sections) {
      counts.set(s.type, (counts.get(s.type) ?? 0) + 1);
    }
    return Object.entries(sectionRegistry.sections)
      .filter(([type, schema]) => {
        if (schema.locked === true) return false;
        const max = schema.maxPerTemplate;
        return max === undefined || (counts.get(type) ?? 0) < max;
      })
      .map(([type]) => type)
      .sort((a, b) =>
        sectionName(a, lang).localeCompare(sectionName(b, lang)),
      );
  }, [draft.sections, lang]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b border-[color:var(--border-light)] px-3 py-2.5">
        <span className="text-[12px] font-bold text-[color:var(--text-primary)]">
          {t(keys.themes.studioSections)}
        </span>
        <button
          type="button"
          onClick={() => setIsAdding((v) => !v)}
          className="rounded-full border border-[color:var(--border-light)] px-2.5 py-1 text-[11px] font-semibold text-[color:var(--text-primary)] hover:border-[color:var(--primary-500)]"
        >
          {isAdding ? t(keys.common.cancel) : t(keys.themes.studioAddSection)}
        </button>
      </div>

      {isAdding ? (
        <div className="max-h-56 overflow-auto border-b border-[color:var(--border-light)] p-2">
          <div className="grid gap-1">
            {addable.map((type) => (
              <button
                key={type}
                type="button"
                onClick={() => {
                  draft.addSection(type);
                  setIsAdding(false);
                }}
                className="rounded-lg px-2.5 py-1.5 text-start text-[12px] text-[color:var(--text-primary)] hover:bg-[color:var(--surface-muted)]"
              >
                {sectionName(type, lang)}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <ul className="flex-1 overflow-auto p-2">
        {draft.sections.map((section: SectionInstance, index) => {
          const schema = sectionRegistry.sections[section.type];
          const isLocked = schema?.locked === true;
          const isSelected = section.id === draft.selectedSectionId;
          const isHidden = section.visible === false;
          return (
            <li key={section.id}>
              <div
                className={`group mb-1 flex items-center gap-1 rounded-lg border px-2 py-1.5 ${
                  isSelected
                    ? "border-[color:var(--primary-500)] bg-[color:var(--primary-100)]"
                    : "border-transparent hover:bg-[color:var(--surface-muted)]"
                }`}
              >
                <button
                  type="button"
                  onClick={() => draft.select(section.id)}
                  className="flex-1 truncate text-start text-[12px] font-medium text-[color:var(--text-primary)]"
                >
                  <span className={isHidden ? "opacity-40" : undefined}>
                    {sectionName(section.type, lang)}
                  </span>
                  {isHidden ? (
                    <span className="ms-1.5 text-[10px] font-semibold uppercase text-[color:var(--text-muted)]">
                      {t(keys.themes.studioHidden)}
                    </span>
                  ) : null}
                </button>

                <button
                  type="button"
                  aria-label={t(keys.themes.studioMoveUp)}
                  title={t(keys.themes.studioMoveUp)}
                  disabled={index === 0}
                  onClick={() => draft.moveSection(section.id, "up")}
                  className="rounded px-1 text-[11px] text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)] disabled:opacity-25"
                >
                  ↑
                </button>
                <button
                  type="button"
                  aria-label={t(keys.themes.studioMoveDown)}
                  title={t(keys.themes.studioMoveDown)}
                  disabled={index === draft.sections.length - 1}
                  onClick={() => draft.moveSection(section.id, "down")}
                  className="rounded px-1 text-[11px] text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)] disabled:opacity-25"
                >
                  ↓
                </button>
                <button
                  type="button"
                  aria-label={t(keys.themes.studioToggleVisible)}
                  title={t(keys.themes.studioToggleVisible)}
                  onClick={() => draft.toggleSectionVisibility(section.id)}
                  className="rounded px-1 text-[11px] text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]"
                >
                  {isHidden ? "○" : "●"}
                </button>
                {/* Locked sections (header / footer / catalog) have no delete
                    button at all rather than a disabled one: the schema says
                    they are structural, so offering the action would imply it
                    is merely unavailable right now. */}
                {isLocked ? null : (
                  <button
                    type="button"
                    aria-label={t(keys.common.delete)}
                    title={t(keys.common.delete)}
                    onClick={() => draft.removeSection(section.id)}
                    className="rounded px-1 text-[11px] text-[color:var(--text-muted)] hover:text-[color:var(--error-600)]"
                  >
                    ✕
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
